/**
 * Admin-only: preset + custom date-range CSV export of per-song
 * profitability. Wired to GET /api/admin/reports/song-profitability.
 *
 * Presets are 30/90 days, YTD, last-12-months. "Custom" reveals two date
 * inputs. Max range = 12 months (backend also enforces).
 */
import { useMemo, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { apiFetch, API } from '../App';
import { toast } from 'sonner';

const PRESETS = [
  { key: 'last30', label: 'Last 30 days', days: 30 },
  { key: 'last90', label: 'Last 90 days', days: 90 },
  { key: 'ytd', label: 'This year', ytd: true },
  { key: 'last12', label: 'Last 12 months', days: 365 },
  { key: 'custom', label: 'Custom range', custom: true },
];

const toISODate = (d) => d.toISOString().slice(0, 10);

const buildRange = (preset, customStart, customEnd) => {
  const today = new Date();
  const end = toISODate(today);
  if (preset.custom) return { start: customStart, end: customEnd };
  if (preset.ytd) {
    const jan1 = new Date(today.getFullYear(), 0, 1);
    return { start: toISODate(jan1), end };
  }
  const start = new Date(today);
  start.setDate(start.getDate() - preset.days);
  return { start: toISODate(start), end };
};

export default function SongProfitabilityExport() {
  const [preset, setPreset] = useState(PRESETS[0]);
  const [customStart, setCustomStart] = useState(
    toISODate(new Date(new Date().setDate(new Date().getDate() - 30)))
  );
  const [customEnd, setCustomEnd] = useState(toISODate(new Date()));
  const [busy, setBusy] = useState(false);

  const range = useMemo(
    () => buildRange(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );

  // Validate custom range: max 366 days, end >= start
  const validation = useMemo(() => {
    if (!range.start || !range.end) return { ok: false, msg: 'Pick both dates' };
    const s = new Date(range.start);
    const e = new Date(range.end);
    if (e < s) return { ok: false, msg: 'End must be after start' };
    const days = Math.ceil((e - s) / (1000 * 60 * 60 * 24));
    if (days > 366) return { ok: false, msg: `Range too wide (${days} days, max 366)` };
    return { ok: true, days };
  }, [range]);

  const download = async () => {
    if (!validation.ok) {
      toast.error(validation.msg);
      return;
    }
    setBusy(true);
    try {
      const url = `${API}/admin/reports/song-profitability?start=${range.start}&end=${range.end}&fmt=csv`;
      const res = await apiFetch(url);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const dl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = dl;
      a.download = `song-profitability-${range.start}-to-${range.end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dl);
      toast.success(`Downloaded report for ${range.start} → ${range.end}`);
    } catch (err) {
      toast.error(`Download failed: ${err.message || err}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 pt-4 border-t border-white/5" data-testid="song-profitability-export">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">
        Song Profitability CSV
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPreset(p)}
            data-testid={`profit-preset-${p.key}`}
            className={`text-xs px-3 py-1.5 rounded border transition-colors ${
              preset.key === p.key
                ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-200'
                : 'bg-black/30 border-white/10 text-zinc-300 hover:border-white/30'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {preset.custom && (
        <div className="flex flex-wrap gap-2 mb-3 items-center text-xs text-zinc-300">
          <label className="flex items-center gap-1">
            Start:
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              data-testid="profit-custom-start"
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white"
            />
          </label>
          <label className="flex items-center gap-1">
            End:
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              data-testid="profit-custom-end"
              className="bg-black/40 border border-white/10 rounded px-2 py-1 text-white"
            />
          </label>
        </div>
      )}

      <div className="flex items-center gap-3">
        <Button
          onClick={download}
          disabled={busy || !validation.ok}
          data-testid="profit-download-btn"
          size="sm"
          variant="secondary"
        >
          {busy ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Preparing…</>
          ) : (
            <><Download className="w-4 h-4 mr-1.5" /> Download CSV</>
          )}
        </Button>
        <span className="text-xs text-zinc-400">
          {validation.ok
            ? `${range.start} → ${range.end}  (${validation.days} days)`
            : validation.msg}
        </span>
      </div>
    </div>
  );
}
